import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, CalendarDays, Search, MessageSquare, Camera } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

const HIDDEN_ROUTES = ['/', '/onboarding'];

const THEME_FOOTER = {
  light:    { grad: ['#507030','rgba(80,112,48,0.98)','rgba(100,138,58,0.9)','rgba(122,158,78,0.75)','rgba(143,184,92,0.55)','rgba(164,199,110,0.35)','rgba(190,218,150,0.18)','rgba(220,235,195,0.06)'], blades: ['#5a7a3a','#6d9048','#4e6e30'] },
  dark:     { grad: ['#2a3a20','rgba(42,58,32,0.98)','rgba(50,70,36,0.9)','rgba(60,82,44,0.75)','rgba(72,96,52,0.55)','rgba(84,110,60,0.35)','rgba(100,128,76,0.18)','rgba(120,148,96,0.06)'], blades: ['#3a5428','#4a6634','#2e4a1e'] },
  forest:   { grad: ['#3a5c28','rgba(58,92,40,0.98)','rgba(72,110,50,0.9)','rgba(88,130,64,0.75)','rgba(106,152,78,0.55)','rgba(128,174,96,0.35)','rgba(158,200,128,0.18)','rgba(190,220,165,0.06)'], blades: ['#4a6e32','#5c8240','#3e6228'] },
  ocean:    { grad: ['#1a5c6a','rgba(26,92,106,0.98)','rgba(34,110,126,0.9)','rgba(46,130,148,0.75)','rgba(62,152,170,0.55)','rgba(82,174,190,0.35)','rgba(120,200,212,0.18)','rgba(165,225,232,0.06)'], blades: ['#2a7080','#3a8896','#1e5e6c'] },
  sunset:   { grad: ['#7a5030','rgba(122,80,48,0.98)','rgba(144,96,58,0.9)','rgba(168,114,72,0.75)','rgba(190,136,90,0.55)','rgba(210,160,112,0.35)','rgba(228,185,142,0.18)','rgba(240,210,178,0.06)'], blades: ['#8a6038','#9e7448','#744e2c'] },
  midnight: { grad: ['#0a1a12','rgba(10,26,18,0.98)','rgba(16,34,24,0.9)','rgba(24,44,32,0.75)','rgba(34,56,42,0.55)','rgba(46,70,54,0.35)','rgba(66,92,74,0.18)','rgba(90,116,98,0.06)'], blades: ['#1a3828','#244a34','#12301e'] },
};

const NAV_ITEMS = [
  { id: 'home', label: 'Home', icon: Home, path: '/dashboard' },
  { id: 'plan', label: 'Plan', icon: CalendarDays, path: '/plan' },
  { id: 'log', label: '', icon: Camera, path: '/log-meal', isCamera: true },
  { id: 'discover', label: 'Discover', icon: Search, path: '/discover' },
  { id: 'coach', label: 'Coach', icon: MessageSquare, path: '/ai-coach' },
];

export const BottomNavBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const ft = THEME_FOOTER[theme] || THEME_FOOTER.light;

  if (HIDDEN_ROUTES.includes(location.pathname)) return null;

  const getActiveTab = () => {
    const p = location.pathname;
    if (p === '/dashboard' || p === '/daily-summary') return 'home';
    if (p.startsWith('/plan') || p === '/meal-plan' || p === '/grocery-list' || p === '/meal-prep' || p === '/recipes' || p === '/meal-templates' || p === '/custom-recipes') return 'plan';
    if (p === '/log-meal' || p === '/quick-log' || p === '/batch-upload') return 'log';
    if (p === '/discover' || p === '/challenges' || p === '/fasting' || p === '/progress-photos' || p === '/accountability' || p === '/progress' || p === '/mode-compare' || p === '/weekly-summary' || p === '/settings' || p === '/voice-journal' || p === '/fridge-scanner') return 'discover';
    if (p === '/ai-coach' || p === '/coach' || p.startsWith('/coach')) return 'coach';
    if (p === '/restaurants' || p === '/restaurant-search') return 'discover';
    return 'home';
  };

  const active = getActiveTab();

  return (
    <nav data-testid="bottom-nav" style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100, height: 90, pointerEvents: 'none' }}>

      {/* Tall meadow gradient that extends well above the nav bar
          This covers the page background so users never see #FDFBF7 behind the grass */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: 220,
        background: `linear-gradient(to top,
          ${ft.grad[0]} 0%,
          ${ft.grad[1]} 8%,
          ${ft.grad[2]} 16%,
          ${ft.grad[3]} 28%,
          ${ft.grad[4]} 40%,
          ${ft.grad[5]} 55%,
          ${ft.grad[6]} 70%,
          ${ft.grad[7]} 85%,
          transparent 100%)`,
        zIndex: 0,
        pointerEvents: 'none',
      }} />

      {/* Grass blades SVG — sits just above the nav bar */}
      <svg
        viewBox="0 0 400 70"
        preserveAspectRatio="none"
        style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          height: 60, zIndex: 1, opacity: 0.75, pointerEvents: 'none',
        }}
      >
        {[
          'M10,70 Q11,42 12,34 Q13,42 14,70',
          'M25,70 Q26,32 28,22 Q30,32 31,70',
          'M42,70 Q43,40 44,32 Q45,40 46,70',
          'M58,70 Q59,28 61,18 Q63,28 64,70',
          'M78,70 Q79,38 80,28 Q81,38 82,70',
          'M95,70 Q96,44 97,36 Q98,44 99,70',
          'M112,70 Q113,30 115,20 Q117,30 118,70',
          'M132,70 Q133,38 134,30 Q135,38 136,70',
          'M148,70 Q149,32 151,22 Q153,32 154,70',
          'M168,70 Q169,40 170,32 Q171,40 172,70',
          'M185,70 Q186,26 188,16 Q190,26 191,70',
          'M205,70 Q206,36 207,28 Q208,36 209,70',
          'M222,70 Q223,42 224,34 Q225,42 226,70',
          'M240,70 Q241,30 243,20 Q245,30 246,70',
          'M258,70 Q259,38 260,30 Q261,38 262,70',
          'M275,70 Q276,24 278,14 Q280,24 281,70',
          'M295,70 Q296,36 297,28 Q298,36 299,70',
          'M312,70 Q313,32 315,22 Q317,32 318,70',
          'M330,70 Q331,40 332,32 Q333,40 334,70',
          'M348,70 Q349,26 351,16 Q353,26 354,70',
          'M365,70 Q366,36 367,28 Q368,36 369,70',
          'M382,70 Q383,32 385,24 Q387,32 388,70',
        ].map((d, i) => (
          <path key={i} d={d} fill={ft.blades[i % 3]} />
        ))}
        <circle cx="35" cy="45" r="2.5" fill="#E5A84B" opacity="0.6" />
        <circle cx="88" cy="38" r="2" fill="#C67A5C" opacity="0.55" />
        <circle cx="155" cy="36" r="2.5" fill="#9B59B6" opacity="0.4" />
        <circle cx="215" cy="42" r="2" fill="#E5A84B" opacity="0.5" />
        <circle cx="270" cy="34" r="2.5" fill="#C67A5C" opacity="0.45" />
        <circle cx="340" cy="40" r="2" fill="#9B59B6" opacity="0.35" />
        <circle cx="390" cy="44" r="2.5" fill="#E5A84B" opacity="0.5" />
      </svg>

      {/* Blur overlay */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: 90, zIndex: 2,
        backdropFilter: 'blur(3px)',
        WebkitBackdropFilter: 'blur(3px)',
        maskImage: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.5) 60%, rgba(0,0,0,0) 100%)',
        WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.5) 60%, rgba(0,0,0,0) 100%)',
        pointerEvents: 'none',
      }} />

      {/* Nav bar */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-around',
        padding: '4px 12px 18px',
        pointerEvents: 'auto',
      }}>
        {NAV_ITEMS.map(item => {
          const Icon = item.icon;
          const isActive = active === item.id;

          if (item.isCamera) {
            return (
              <button
                key={item.id}
                data-testid="nav-log-meal-btn"
                onClick={() => navigate(item.path)}
                style={{
                  width: 54, height: 54, borderRadius: '50%',
                  background: 'linear-gradient(145deg, #5A8C6A, #4A7A5A)',
                  border: '3px solid rgba(253,251,247,0.8)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginTop: -22, cursor: 'pointer',
                  boxShadow: '0 6px 20px rgba(90,140,106,0.35), 0 2px 6px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.2)',
                  transition: 'transform 0.15s ease',
                }}
                onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.93)'; }}
                onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
              >
                <Icon style={{ width: 22, height: 22, color: '#FFF', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.15))' }} />
              </button>
            );
          }

          return (
            <button
              key={item.id}
              data-testid={`nav-${item.id}-btn`}
              onClick={() => navigate(item.path)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                padding: '4px 8px',
                fontFamily: 'var(--font-sans)', fontSize: 10, letterSpacing: '0.3px',
                fontWeight: isActive ? 600 : 400,
                color: isActive ? '#2d4a1c' : '#4a5a3a',
                transition: 'color 0.2s ease',
              }}
            >
              <div style={{
                width: 36, height: 36, borderRadius: 12,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: isActive ? 'rgba(253,251,247,0.95)' : 'rgba(245,242,236,0.9)',
                boxShadow: '0 2px 6px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)',
                transition: 'all 0.2s ease',
              }}>
                <Icon style={{ width: 18, height: 18, strokeWidth: isActive ? 2.2 : 1.8, color: isActive ? '#2d4a1c' : '#4a5a3a' }} />
              </div>
              <span style={{
                color: isActive ? '#FDFBF7' : 'rgba(253,251,247,0.85)',
                textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                fontWeight: isActive ? 700 : 500,
              }}>{item.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};
